import React from 'react';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import withStyles from '@mui/styles/withStyles';
import makeStyles from '@mui/styles/makeStyles';

import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import DeleteIcon from '@mui/icons-material/Delete';
import CollapsedIcon from '@mui/icons-material/ChevronRight';

import { addOrUpdateVobFormInfo, removeVobFormInfo } from '../store/slices/vobFormInfosSlice';

import { createStdRequest, apiFetch } from '../utils/utils';

import DA from '../store/ReduxAccess';

import VobForm from './VobForm';
import CrmButton from './CrmButton';
import CrmSnackbar from './CrmSnackbar';

const blankInfo = {
  verified: false,
  verificationDate: null,
  representativeName: '',
  verificationNumber: '',
  relationship: 'Choose...',
  customRelationship: '',
  holderFirstName: '',
  holderLastName: '',
  birthDate: null,
  holderStreet: '',
  holderCity: '',
  holderState: 'State',
  holderZip: '',
  insuranceCarrier: '',
  policyId: '',
  groupId: '',
  planType: 'Choose...',
  effectiveDate: null,
  renewalDate: null,
  policyYear: 'calendarYear',
  policyIsActive: false,
  usingCobra: false,
  acceptsAssignment: false,
  cobOnFile: false,
  cobRequired: false,
  lastCobUpdated: null,
  individualDeductible: '',
  individualDeductibleMet: '',
  individualOop: '',
  individualOopMet: '',
  familyDeductible: '',
  familyDeductibleMet: '',
  familyOop: '',
  familyOopMet: '',
  copayAmount: '',
  coinsurancePct: '',
  limitStatus: '',
  reimbursementBasis: 'Choose...',
  deductibleAppliesToOop: false,
  freestandingCovored: false,
  jcahoRequred: false,
  outOfStateBenefits: false,
  stateLicenseRequred: false,
  exclusionsP: false,
  exclusionsText: '',
  preauthPenaltyP: false,
  preauthPenaltyText: '',
  preauthCompany: '',
  preauthCompanyPhone: '',
  networkStatus: 'Choose...',
  preauthRequred_OP: false,
  covered_OP: false,
  coinsurance_OP: '',
  copay_OP: '',
  benefitMax_OP: '',
  amountUsed_OP: '',
  preauthRequred_IOP: false,
  covered_IOP: false,
  coinsurance_IOP: '',
  copay_IOP: '',
  benefitMax_IOP: '',
  amountUsed_IOP: '',
  preauthRequred_PHP: false,
  covered_PHP: false,
  coinsurance_PHP: '',
  copay_PHP: '',
  benefitMax_PHP: '',
  amountUsed_PHP: '',
  preauthRequred_RTC: false,
  covered_RTC: false,
  coinsurance_RTC: '',
  copay_RTC: '',
  benefitMax_RTC: '',
  amountUsed_RTC: '',
  preauthRequred_Detox: false,
  covered_Detox: false,
  coinsurance_Detox: '',
  copay_Detox: '',
  benefitMax_Detox: '',
  amountUsed_Detox: '',
  notes: '',
  shouldSpecifyCarrier: false,
  specifiedCarrier: '',
};

const localStyles = (theme) => ({
  bottomButtonDiv: {
    textAlign: 'right',
    marginRight: '34px',
  },
});

/*
Call this with
    <VobFormList
        clientId={whatever (0 if on initial call page)}
        clientInfo={{
          clientFirstName: prospectFirstName,
          clientLastName: prospectLastName,
          clientBirthDate: prospectBirthDate,
          clientAddressLine1: prospectAddressLine1,
          clientCity: prospectCity,
          clientZip: prospectZip,
          clientState: prospectState,
        }}
        closeModalFunc={a function for closing the dialog}
    />
*/

class VobFormList extends React.Component {
  state = {
    vobInfos: [],
    collapsedGuids: [], // guids of infos of forms that should be collapsed
    snackbar: {
      isOpen: false,
      message: '',
    },
  };

  componentDidMount() {
    this.loadVobInfos();
  }

  createBlankInfo() {
    const stateBeforeGuid = blankInfo;
    const stateWithGuid = { ...stateBeforeGuid, guid: uuidv4() };
    return stateWithGuid;
  }

  loadVobInfos() {
    const clientId = this.props.clientId;
    if (clientId === 0) {
      const reduxVobInfos = DA.getVobFormInfosForPersonWithId(0);
      // should be an array of object representations of forms
      if (reduxVobInfos.length > 0) {
        this.setState({ vobInfos: reduxVobInfos });
      } else {
        const blank = this.createBlankInfo();
        this.props.addOrUpdateVobFormInfo({ personId: 0, vobFormInfo: blank });
        this.setState({ vobInfos: [blank] });
      }
    } else {
      const uri = `/api/person/${clientId}/vobFormInfo`;
      const request = createStdRequest(uri);
      const actionOnGoodResponse = (fetchedVobInfos) => {
        if (fetchedVobInfos.length > 0) {
          // we'll store the fetchedInfos (without resolved dates) into Redux
          const fetchedInfosWithGuids = fetchedVobInfos.map((info) => this.withGuidIfMissing(info));
          fetchedInfosWithGuids.forEach((info) => {
            this.props.addOrUpdateVobFormInfo({ personId: clientId, vobFormInfo: info });
          });
          const resolvedInfos = fetchedInfosWithGuids.map((info) => DA.resolveVobFormInfoDates(info));
          this.setState({ vobInfos: resolvedInfos });
        } else {
          this.addNewForm();
        }
      };
      apiFetch(this, request, actionOnGoodResponse);
    }
  }

  withGuidIfMissing(info) {
    return info.guid ? info : { ...info, guid: uuidv4() };
  }

  addNewForm() {
    const blankInfo = this.createBlankInfo();
    this.saveNewVobInfo(blankInfo);
  }

  collapse(infoGuid, collapsed) {
    const listWithoutThisGuid = this.state.collapsedGuids.filter((item) => item !== infoGuid);
    if (collapsed) {
      const newList = [...listWithoutThisGuid, infoGuid];
      this.setState({ collapsedGuids: newList });
    } else {
      this.setState({ collapsedGuids: listWithoutThisGuid });
    }
  }

  saveNewVobInfo(info) {
    this.saveNewOrUpdatedVobInfo(info, 'POST');
  }

  saveUpdatedVobInfo(info) {
    this.saveNewOrUpdatedVobInfo(info, 'PUT');
    this.showSnackbar();
  }

  saveNewOrUpdatedVobInfo(info, method) {
    // TODO update the Redux store for this formInfo
    // TODO if we've already got a client, update the DB, too.
    const clientId = this.props.clientId;
    if (clientId === 0) {
      this.props.addOrUpdateVobFormInfo({ personId: 0, vobFormInfo: info });
      const reduxVobInfos = DA.getVobFormInfosForPersonWithId(0);
      this.setState({ vobInfos: reduxVobInfos });
    } else {
      const guid = info.guid;
      let uri = `/api/person/${clientId}/vobFormInfo`;
      if (method === 'PUT') {
        uri += `/${guid}`;
      }
      const apiStruct = {
        guid,
        vobFormInfo: JSON.stringify(info),
      };
      const request = createStdRequest(uri, method, apiStruct);
      const actionOnGoodResponse = () => {
        this.props.addOrUpdateVobFormInfo({ personId: clientId, vobFormInfo: info });
        const reduxVobInfos = DA.getVobFormInfosForPersonWithId(clientId);
        this.setState({ vobInfos: reduxVobInfos });
      };
      apiFetch(this, request, actionOnGoodResponse);
    }
  }

  deleteVobInfo(info) {
    const clientId = this.props.clientId;
    if (clientId === 0) {
      this.props.removeVobFormInfo({ personId: 0, vobFormInfo: info });
      const reduxVobInfos = DA.getVobFormInfosForPersonWithId(0);
      this.setState({ vobInfos: reduxVobInfos });
    } else {
      const guid = info.guid;
      const uri = `/api/person/${clientId}/vobFormInfo/${guid}`;
      const request = createStdRequest(uri, 'DELETE');
      const actionOnGoodResponse = () => {
        this.props.removeVobFormInfo({ personId: clientId, vobFormInfo: info });
        const reduxVobInfos = DA.getVobFormInfosForPersonWithId(clientId);
        this.setState({ vobInfos: reduxVobInfos });
      };
      apiFetch(this, request, actionOnGoodResponse);
    }
  }

  saveAndClose() {
    this.props.closeModalFunc();
    // this.state.vobInfos.forEach(info => this.saveUpdatedVobInfo(info));
  }

  showSnackbar() {
    this.setState({
      snackbar: {
        message: 'Form saved',
        isOpen: true,
      },
    });
  }

  render() {
    const { classes, clientId, clientInfo } = this.props;
    const m = this.state.vobInfos.length;
    return (
      <div>
        <CrmSnackbar renderer={this} options={this.state.snackbar} />
        {this.state.vobInfos.map((info, i) => (
          <VobInfoItem
            key={info.guid}
            vobInfo={info}
            nOfM={`(${i + 1} of ${m})`}
            clientId={clientId}
            clientInfo={clientInfo}
            collapsed={this.state.collapsedGuids.find((item) => item === info.guid)}
            collapseFunc={(guid, collapsed) => this.collapse(guid, collapsed)}
            saveFunc={(newInfo) => this.saveUpdatedVobInfo(newInfo)}
            deleteFunc={(info) => this.deleteVobInfo(info)}
          />
        ))}
        <div className={classes.bottomButtonDiv}>
          <BottomButton outlined={true} tip={'Add a Form'} text={'+ New VOB Form'} action={() => this.addNewForm()} />
          <BottomButton tip={'Close Dialog'} text={'Close'} action={() => this.saveAndClose()} />
        </div>
      </div>
    );
  }
}

const VobInfoItem = ({ vobInfo, nOfM, clientId, clientInfo, collapsed, collapseFunc, saveFunc, deleteFunc }) => {
  const useFormStub = false;
  if (collapsed) {
    return <VobInfoSummaryStub vobInfo={vobInfo} collapseFunc={collapseFunc} deleteFunc={deleteFunc} />;
  } else if (useFormStub) {
    return <VobFormStub vobInfo={vobInfo} collapseFunc={collapseFunc} saveFunc={saveFunc} deleteFunc={deleteFunc} />;
  } else {
    return (
      <VobForm
        vobInfo={vobInfo}
        nOfM={nOfM}
        clientId={clientId}
        clientInfo={clientInfo}
        collapseFunc={collapseFunc}
        saveFunc={saveFunc}
        deleteFunc={deleteFunc}
      />
    );
  }
};

const actionProducers = { addOrUpdateVobFormInfo, removeVobFormInfo };

export default connect(null, actionProducers)(withStyles(localStyles)(VobFormList));

const useSummaryStyles = makeStyles((theme) => ({
  root: {
    border: '2px solid black',
    margin: '4px 2px ',
    padding: '4px',
  },
  expandButtonSpan: {
    paddingRight: '4px',
  },
  expandIconButton: {
    padding: 0,
  },
  withEllipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  trashIconDiv: {
    textAlign: 'right',
    paddingRight: '32px',
  },
  trashIconButton: {
    padding: 0,
  },
}));

const VobInfoSummaryStub = ({ vobInfo, collapseFunc, deleteFunc }) => {
  const classes = useSummaryStyles();

  const carrier = vobInfo.insuranceCarrier;
  const carrierNameToShow = carrier || 'Unknown Carrier';
  const verifiedText = vobInfo.verified ? 'Verified' : 'Not Yet Verified';
  const policyIdText = vobInfo.policyId ? 'Policy ID: ' + vobInfo.policyId : 'No Policy ID yet';
  return (
    <div className={classes.root}>
      <Grid container>
        <Grid item md={6} className={classes.withEllipsis}>
          <span className={classes.expandButtonSpan}>
            <IconButton
              className={classes.expandIconButton}
              onClick={() => collapseFunc(vobInfo.guid, false)}
              size="large"
            >
              <CollapsedIcon />
            </IconButton>
          </span>
          {carrierNameToShow}
        </Grid>
        <Grid item md={2} className={classes.withEllipsis}>
          {verifiedText}
        </Grid>
        <Grid item md={3} className={classes.withEllipsis}>
          {policyIdText}
        </Grid>
        <Grid item md={1} className={classes.trashIconDiv}>
          <IconButton className={classes.trashIconButton} onClick={() => deleteFunc(vobInfo)} size="large">
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    </div>
  );
};

const VobFormStub = ({ vobInfo, collapseFunc, saveFunc, deleteFunc }) => {
  return (
    <div>
      v This represents an open form for info: {vobInfo.guid}
      <div>
        <button onClick={() => collapseFunc(vobInfo.guid, true)}>Collapse</button>
        <button onClick={() => saveFunc(vobInfo)}>Save</button>
        <button onClick={() => deleteFunc(vobInfo)}>Delete</button>
      </div>
    </div>
  );
};

const useBottomButtonStyles = makeStyles((theme) => ({
  buttonSpan: {
    marginLeft: '24px',
  },
  tooltip: {
    /*
    padding: '10px 15px',
    minWidth: '130px',
    color: whiteColor,
    lineHeight: '1.7em',
    background: 'rgba(' + hexToRgb(grayColor[6]) + ',0.9)',
    border: 'none',
    borderRadius: '3px',
    opacity: '1!important',
    boxShadow:
      '0 8px 10px 1px rgba(' +
      hexToRgb(blackColor) +
      ', 0.14), 0 3px 14px 2px rgba(' +
      hexToRgb(blackColor) +
      ', 0.12), 0 5px 5px -3px rgba(' +
      hexToRgb(blackColor) +
      ', 0.2)',
    maxWidth: '200px',
    textAlign: 'center',
    // fontFamily: '"Helvetica Neue",Helvetica,Arial,sans-serif',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '400',
    textShadow: 'none',
    textTransform: 'none',
    letterSpacing: 'normal',
    wordBreak: 'normal',
    wordSpacing: 'normal',
    wordWrap: 'normal',
    whiteSpace: 'normal',
    lineBreak: 'auto',*/
  },
  collapseButton: {
    margin: '0 0 0 -20px',
    transform: 'scale(1.3)',
    '& svg,& .fab,& .fas,& .far,& .fal,& .material-icons': {
      marginRight: '0px',
    },
  },
}));

const BottomButton = ({ outlined, tip, text, action }) => {
  const classes = useBottomButtonStyles();

  return (
    <span className={classes.buttonSpan}>
      <Tooltip title={tip} placement="bottom" classes={{ tooltip: classes.tooltip }}>
        <CrmButton outlined={outlined} filled={!outlined} onClick={action}>
          {text}
        </CrmButton>
      </Tooltip>
    </span>
  );
};
